import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export type EntitlementKey =
  | "workout_builder"
  | "workout_presets"
  | "threshold_tests"
  | "race_team_create"
  | "race_team_invite"
  | "race_team_role_assign"
  | "pair_stats"
  | "friend_stats"
  | "forecast_loop";

export type Entitlement = {
  key: EntitlementKey;
  allowed: boolean;
  /** Minimum tier this feature currently needs — paywall copy reads it from
   *  here instead of hardcoding "Pro", so a feature the creator moves on the
   *  ladder does not leave the UI advertising the wrong tier. */
  required_tier: "free" | "pro" | "elite";
  kind: "hard" | "soft";
};

export type EntitlementsPayload = {
  tier: "free" | "pro" | "elite" | "coached";
  features: Entitlement[];
};

/**
 * Access decisions, made server-side.
 *
 * Replaces deriving access from the tier in the client. `useAthleteEffectiveTier`
 * still exists and is still right for *showing* the tier ("ты на Pro"), but it
 * must not decide what is reachable: the server rule has layers the tier alone
 * cannot express (build mode, per-feature ladder position, individual grants),
 * and a client that re-derives the verdict will disagree with the backend the
 * moment any of them is used — hiding buttons for requests the API would accept.
 */
export function useEntitlements() {
  return useQuery<EntitlementsPayload>({
    queryKey: ["athlete-entitlements"],
    queryFn: async () => {
      const r = await api.get("/athlete/entitlements");
      return r.data as EntitlementsPayload;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * One feature's verdict.
 *
 * While loading, `allowed` is false and `isLoading` is true — callers that hide
 * an entry point should wait for `isLoading` to clear rather than flash the
 * locked state at an athlete who actually has access.
 */
export function useEntitlement(key: EntitlementKey): {
  allowed: boolean;
  requiredTier: string | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useEntitlements();
  const row = data?.features.find((f) => f.key === key);
  return {
    allowed: row?.allowed === true,
    requiredTier: row?.required_tier,
    isLoading,
  };
}
